//
// Searchbox Controller
// ---------------------------------
// This controller is responsible for the search bar autocomplete functionality.
//
// It is responsible for the following:
// - Setting up the search bar autocomplete functionality
// - Fetching the search bar autocomplete data from the server
// - Updating the search bar autocomplete data when the user types in the search bar
//

import { Controller } from "@hotwired/stimulus";
// import { fetch } from "@rails/request.js"
import Combobox from "@github/combobox-nav";
import {
  ensureGeocoder,
  geocodeUsAddress,
} from "../../services/appleMapService";

export default class extends Controller {
  static targets = ["container", "input", "list"];
  static values = {
    active: { type: Boolean, default: false },
    placeholder: String, // Placeholder when active (e.g., "Buffalo, NY")
    placeholderInactive: String, // Placeholder when inactive (e.g., "Churches in Buffalo, NY")
    geocodeFallback: { type: Boolean, default: true }, // Enable geocode fallback for addresses
  };

  connect() {
    // Close dropdown before Turbo caches the page
    this.handleBeforeCache = () => {
      if (this.activeValue) {
        this.stop();
      }
    };
    document.addEventListener("turbo:before-cache", this.handleBeforeCache);

    // Also handle bfcache restoration (browser back/forward cache)
    this.handlePageShow = (event) => {
      if (event.persisted && this.activeValue) {
        this.stop();
      }
    };
    window.addEventListener("pageshow", this.handlePageShow);

    // Cmd+K (Mac) / Ctrl+K (Windows) to open search
    this.handleKeyboardShortcut = (event) => {
      if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        this.start();
      }
    };
    document.addEventListener("keydown", this.handleKeyboardShortcut);
  }

  disconnect() {
    if (this.handleBeforeCache) {
      document.removeEventListener(
        "turbo:before-cache",
        this.handleBeforeCache,
      );
    }
    if (this.handlePageShow) {
      window.removeEventListener("pageshow", this.handlePageShow);
    }
    if (this.handleKeyboardShortcut) {
      document.removeEventListener("keydown", this.handleKeyboardShortcut);
    }
    this.combobox?.destroy();
  }

  listTargetConnected(target) {
    // console.log('listTargetConnected')
    if (this.activeValue) {
      this.start();
    }
    // Check for geocode fallback after autocomplete results render
    this.checkGeocodeFallback();
  }

  // Start or restart combobox when list is connected
  // Prevents stale comboboxes from being used
  start() {
    // console.log('started SearchBarController')
    this.activeValue = true;
    this.combobox?.destroy();
    this.combobox = new Combobox(this.inputTarget, this.listTarget);
    this.combobox.start();

    // Set active state of searchbox
    // This is used to style the searchbox when it is active
    // This is done here because the combobox is not available until after the list is connected
  }

  stop() {
    this.combobox?.stop();
    this.activeValue = false;
    this.inputTarget.blur();
    // this.setActive(false)
  }

  // This is called when the user clicks on the search bar
  // It is used to start the combobox
  // It is also used to hide the keyboard on mobile
  // This is done here because the combobox is not available until after the list is connected
  activeValueChanged(value, oldValue) {
    // console.log('setActiveChanged', value, oldValue)
    // if prev value is undefined or empty, do nothing
    if (oldValue == undefined || oldValue == null) {
      // console.log('setActiveChanged DO NOTHING', value, oldValue)
      return;
    }
    if (value == true) {
      this.containerTarget.classList.add("active");
      this.inputTarget.focus();
      // Swap to active placeholder
      if (this.hasPlaceholderValue) {
        this.inputTarget.placeholder = this.placeholderValue;
      }

      this.listTarget.addEventListener("combobox-commit", handleCommit);
      this._setInert(true);
    } else {
      this.listTarget.removeEventListener("combobox-commit", handleCommit);
      this._setInert(false);
      // remove active class
      this.containerTarget.classList.remove("active");
      // Swap to inactive placeholder
      if (this.hasPlaceholderInactiveValue) {
        this.inputTarget.placeholder = this.placeholderInactiveValue;
      }
    }
  }

  hideKeyboard() {
    // console.log('hideKeyboard')
    if (this.activeValue) {
      this.inputTarget.blur();
    }
  }

  _setInert(value) {
    for (const el of this.element.parentElement.children) {
      if (el !== this.element && el.nodeType === 1) el.inert = value;
    }
    const inner = this.element.querySelector(".navbar__inner");
    if (!inner) return;
    for (const child of inner.children) {
      if (child !== this.containerTarget) {
        child.inert = value;
      }
    }
  }

  // Geocode fallback: when no Location results exist, try MapKit geocoding
  checkGeocodeFallback() {
    if (!this.geocodeFallbackValue) return;
    if (!this.hasListTarget) return;

    // Cancel any pending geocode request
    this.pendingGeocodeQuery = null;

    const hasLocationResults = this.listTarget.querySelector(
      "[data-result-type='location']",
    );
    if (hasLocationResults) return;

    const query = this.inputTarget.value?.trim();
    if (!query || query.length < 3) return;

    this.geocodeAndInject(query);
  }

  async geocodeAndInject(query) {
    // Store query to detect if a newer request should supersede this one
    this.pendingGeocodeQuery = query;

    try {
      await ensureGeocoder();

      // Check if query is still current (user may have typed more)
      if (this.pendingGeocodeQuery !== query) return;

      const result = await geocodeUsAddress(query);

      // Check again after async operation
      if (this.pendingGeocodeQuery !== query) return;

      if (result) {
        this.injectGeocodeResult(result, query);
      }
    } catch (e) {
      console.error("Searchbox geocode fallback failed:", e);
    }
  }

  injectGeocodeResult(result, query) {
    if (!this.hasListTarget) return;

    // Check if we already injected a geocode result
    const existing = this.listTarget.querySelector(
      "[data-geocode-result='true']",
    );
    if (existing) existing.remove();

    // Create location-styled list item matching the server-rendered format
    const li = document.createElement("li");
    li.className = "list-group-item p-0";
    li.setAttribute("data-geocode-result", "true");
    li.setAttribute("data-result-type", "location");

    const url = `/search?lat=${result.lat}&lng=${result.lng}&location=${encodeURIComponent(result.displayName)}`;
    li.innerHTML = `
      <a role="option" href="${url}" class="list-group-item-action" tabindex="-1">
        <div class="list-group-item-content d-flex">
          <div class="list-group-item-icon">
            <i class="fa-regular fa-location-dot"></i>
          </div>
          <div class="list-group-item-body">
            <h5 class="list-group-item-heading">${this.escapeHtml(result.displayName)}</h5>
            <div class="list-group-item-text">Address</div>
          </div>
        </div>
      </a>
    `;

    // Insert at the beginning of the list
    this.listTarget.insertBefore(li, this.listTarget.firstElementChild);

    // Restart combobox to include the new item in keyboard navigation
    if (this.combobox) {
      this.combobox.stop();
      this.combobox.start();
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}

function handleCommit(event) {
  // console.log('combobox-commit', event)
  const firstLink = event.target.querySelector("a");
  if (firstLink) {
    firstLink.click();
  }
}
